import type { Product } from "../marketplace/shopApi";

// The two 450labs products behind AOTD prints. They have no photos: the site
// shows the day's art and title from n450s_backend instead. Every variant is at
// stock 0 in 450labs while prints are pre-order only, so no order can be placed
// for them, even straight through the 450labs API.

export type PrintFormat = "trading-card" | "wall-card" | "poster";
export type PrintEdition = "print" | "original";

export const PRINT_PRODUCT_ID = "870f8e51-1ce3-46ce-b37b-1fad99b31bfd";
export const ORIGINAL_PRODUCT_ID = "ede57aca-5900-41c1-bc88-ca8c360d8732";
export const PRINT_PRODUCT_IDS = [PRINT_PRODUCT_ID, ORIGINAL_PRODUCT_ID];

export interface FormatInfo {
  format: PrintFormat;
  name: string;
  size: string;
  /** Used until the 450labs catalogue lists the product. */
  fallbackCents: number;
  variantIds: Partial<Record<PrintEdition, string>>;
}

export const FORMATS: FormatInfo[] = [
  {
    format: "trading-card",
    name: "trading card",
    size: "63 x 88 mm",
    fallbackCents: 450,
    variantIds: {
      print: "3a14f2bd-e3ff-4d71-b91b-36141e1560e4",
      original: "feefa475-aed8-49c7-833e-b075eaa85f2f",
    },
  },
  {
    format: "wall-card",
    name: "wall card",
    size: "21 x 21 cm",
    fallbackCents: 2100,
    variantIds: {
      print: "a894d7c4-89c0-418c-ac14-fceb7acf9c97",
      original: "12f25017-a00f-42b6-bcb0-91f9968e28f8",
    },
  },
  {
    format: "poster",
    name: "poster",
    size: "50 x 50 cm",
    fallbackCents: 4500,
    // no original at poster size: digital foil can't take a 50 cm sheet
    variantIds: { print: "ea1b5f46-d5d1-4cb9-ab4d-bb6f1ec74d73" },
  },
];

export const isPrintProduct = (productId: string) => PRINT_PRODUCT_IDS.includes(productId);

/** The price of one format and edition, from the catalogue when it lists the product. */
export function formatPriceCents(products: Product[], info: FormatInfo, edition: PrintEdition): number {
  const productId = edition === "original" ? ORIGINAL_PRODUCT_ID : PRINT_PRODUCT_ID;
  const product = products.find((p) => p.id === productId);
  const variant = product?.variants?.find((v) => v.id === info.variantIds[edition]);
  if (!product || !variant) return info.fallbackCents;
  return product.price_cents + variant.price_delta_cents;
}

/** The cheapest print, for "from €4.50". */
export function lowestPrintCents(products: Product[]): number {
  return Math.min(...FORMATS.map((f) => formatPriceCents(products, f, "print")));
}
